import React, { useRef, useEffect } from 'react';

// Note-to-y-position mapping (higher notes = higher position)
const NOTE_ORDER = [
  'Bb3','B3','C4','C#4','D4','Eb4','E4','F4','F#4','G4','G#4','A4','Bb4','B4',
  'C5','C#5','D5','Eb5','E5','F5','F#5','G5','G#5','A5','Bb5','B5',
  'C6','C#6','D6','Eb6','E6','F6','F#6'
];

const NOTE_COLORS = {
  low: '#90CAF9',     // blue for low register
  mid: '#FFE082',     // yellow for middle register
  high: '#EF9A9A',    // red for high register
};

function getNoteY(noteName, canvasHeight) {
  const idx = NOTE_ORDER.indexOf(noteName);
  if (idx < 0) return canvasHeight / 2;
  const padding = 30;
  const usable = canvasHeight - padding * 2;
  return canvasHeight - padding - (idx / (NOTE_ORDER.length - 1)) * usable;
}

function getNoteColor(noteName) {
  const idx = NOTE_ORDER.indexOf(noteName);
  if (idx < 0) return '#888';
  if (idx < 14) return NOTE_COLORS.low;
  if (idx < 26) return NOTE_COLORS.mid;
  return NOTE_COLORS.high;
}

function PianoRoll({ notes, currentNoteIndex, onSeek }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !notes || notes.length === 0) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;

    // Clear
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, width, height);

    // Draw grid lines for note positions
    ctx.strokeStyle = '#252540';
    ctx.lineWidth = 0.5;
    for (let i = 0; i < NOTE_ORDER.length; i += 2) {
      const y = getNoteY(NOTE_ORDER[i], height);
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Calculate note positions
    const barWidth = 60; // pixels per beat
    const noteGap = 2;
    let totalBeats = 0;
    const notePositions = notes.map((note) => {
      const beats = note.durationBeats || note.DurationBeats || 1;
      const x = totalBeats * barWidth;
      const w = beats * barWidth - noteGap;
      totalBeats += beats;
      return { x, w, beats };
    });

    // Scroll offset to keep current note visible
    const cursorX = currentNoteIndex >= 0 && notePositions[currentNoteIndex]
      ? notePositions[currentNoteIndex].x
      : 0;
    const scrollOffset = Math.max(0, cursorX - width * 0.3);

    // Draw notes
    notes.forEach((note, i) => {
      const noteName = note.noteName || note.NoteName;
      const isRest = note.isRest || note.IsRest;
      const pos = notePositions[i];
      const x = pos.x - scrollOffset;
      const w = pos.w;

      if (x + w < -10 || x > width + 10) return; // skip off-screen

      if (isRest) {
        // Draw rest as a dimmed bar at center
        const y = height / 2 - 4;
        ctx.fillStyle = i === currentNoteIndex ? '#555' : '#333';
        ctx.fillRect(x, y, w, 8);
        ctx.fillStyle = '#777';
        ctx.font = '9px monospace';
        ctx.fillText('rest', x + 2, y - 2);
      } else {
        const y = getNoteY(noteName, height);
        const barHeight = 14;
        const color = getNoteColor(noteName);

        ctx.fillStyle = i === currentNoteIndex ? '#fff' : color;
        ctx.globalAlpha = i === currentNoteIndex ? 1.0 : (i < currentNoteIndex ? 0.4 : 0.8);
        ctx.beginPath();
        ctx.roundRect(x, y - barHeight / 2, w, barHeight, 3);
        ctx.fill();
        ctx.globalAlpha = 1.0;

        // Note label
        if (w > 20) {
          ctx.fillStyle = i === currentNoteIndex ? '#1a1a2e' : '#fff';
          ctx.font = 'bold 9px monospace';
          ctx.fillText(noteName, x + 4, y + 3);
        }
      }
    });

    // Cursor line
    if (currentNoteIndex >= 0 && notePositions[currentNoteIndex]) {
      const cx = notePositions[currentNoteIndex].x - scrollOffset;
      ctx.strokeStyle = '#FFD700';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx, 0);
      ctx.lineTo(cx, height);
      ctx.stroke();

      // Glow effect
      ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.moveTo(cx, 0);
      ctx.lineTo(cx, height);
      ctx.stroke();
    }

    // Note scale labels on the left
    ctx.fillStyle = '#555';
    ctx.font = '8px monospace';
    for (let i = 0; i < NOTE_ORDER.length; i += 4) {
      const y = getNoteY(NOTE_ORDER[i], height);
      ctx.fillText(NOTE_ORDER[i], 2, y + 3);
    }

  }, [notes, currentNoteIndex]);

  const handleClick = (e) => {
    if (!notes || notes.length === 0 || !onSeek) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    // Simple: find closest note based on proportional position
    const fraction = clickX / rect.width;
    const index = Math.floor(fraction * notes.length);
    onSeek(Math.max(0, Math.min(index, notes.length - 1)));
  };

  return (
    <div className="piano-roll" ref={containerRef}>
      <canvas ref={canvasRef} onClick={handleClick} style={{ cursor: 'pointer' }} />
    </div>
  );
}

export default PianoRoll;
